import { OPEN_ACCOUNT_NAME_MAX_LENGTH } from '@/features/openTabs/constants'
import useAccountRecords from '@/features/openTabs/contexts/OpenTabsData/hooks/useAccountRecords'
import { logger } from '@/features/openTabs/logger'
import { OpenTabCreateDocument } from '@/features/openTabs/mutations'
import { AccountStatus } from '@/features/openTabs/types'
import { useHandleErrorWithToast } from '@/hooks/useHandleErrorWithToast'
import { useMutation } from '@apollo/client'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'

interface UseAccountSelectionArgs {
    records: ReturnType<typeof useAccountRecords>
    clearCatalogCart: () => void
    clearPaymentSelection: () => void
    settlingAccount: boolean
}

const useAccountSelection = ({
    records,
    clearCatalogCart,
    clearPaymentSelection,
    settlingAccount,
}: UseAccountSelectionArgs) => {
    const { t } = useTranslation('app')
    const handleError = useHandleErrorWithToast(logger)
    const createAccountInFlightRef = useRef(false)
    const {
        value: { accounts, activeStatus, selectedAccountId },
        sourceReady,
        selectedRecord,
        selectedAccountCancelled,
        refetchSummaries,
        rememberCreatedTab,
        setActiveStatusState,
        setSelectedAccountId,
    } = records

    useEffect(() => {
        if (!selectedAccountId || !sourceReady || settlingAccount) return

        if (!selectedRecord || selectedAccountCancelled) {
            setSelectedAccountId(null)
            clearPaymentSelection()
            clearCatalogCart()
            return
        }

        if (selectedRecord.state !== 'cancelled' && selectedRecord.state !== activeStatus) {
            setActiveStatusState(selectedRecord.state)
            clearPaymentSelection()
            clearCatalogCart()
        }
    }, [
        activeStatus,
        clearCatalogCart,
        clearPaymentSelection,
        selectedAccountId,
        selectedAccountCancelled,
        setActiveStatusState,
        setSelectedAccountId,
        sourceReady,
        selectedRecord,
        settlingAccount,
    ])

    const [createOpenTab, { loading: creatingAccount }] = useMutation(OpenTabCreateDocument, {
        onError: (error) => void handleError(error),
    })
    const setActiveStatus = useCallback(
        (status: AccountStatus) => {
            setActiveStatusState(status)
            setSelectedAccountId(null)
            clearPaymentSelection()
            clearCatalogCart()
        },
        [clearCatalogCart, clearPaymentSelection, setActiveStatusState, setSelectedAccountId]
    )

    const selectAccount = useCallback(
        (accountId: string) => {
            const account = accounts.find((candidate) => candidate.id === accountId)
            if (!account) return

            setActiveStatusState(account.status)
            setSelectedAccountId(account.id)
            clearPaymentSelection()
            clearCatalogCart()
        },
        [
            accounts,
            clearCatalogCart,
            clearPaymentSelection,
            setActiveStatusState,
            setSelectedAccountId,
        ]
    )

    const clearSelection = useCallback(() => {
        setSelectedAccountId(null)
        clearPaymentSelection()
        clearCatalogCart()
    }, [clearCatalogCart, clearPaymentSelection, setSelectedAccountId])

    const createAccount = useCallback(
        async (name: string): Promise<string | undefined> => {
            const resolvedName = name.trim() || t('openTabs.createAccount.defaultName')
            if (
                resolvedName.length > OPEN_ACCOUNT_NAME_MAX_LENGTH ||
                creatingAccount ||
                createAccountInFlightRef.current
            ) {
                return undefined
            }

            createAccountInFlightRef.current = true
            try {
                const result = await createOpenTab({
                    variables: { input: { name: resolvedName } },
                })
                const tab = result.data?.openTabCreate
                if (!tab?.uuid) return undefined
                const id = tab.uuid

                rememberCreatedTab(tab)
                clearCatalogCart()
                clearPaymentSelection()
                setActiveStatusState('open')
                setSelectedAccountId(id)
                void refetchSummaries().catch(() => undefined)

                return id
            } catch {
                return undefined
            } finally {
                createAccountInFlightRef.current = false
            }
        },
        [
            clearCatalogCart,
            clearPaymentSelection,
            createOpenTab,
            creatingAccount,
            refetchSummaries,
            rememberCreatedTab,
            setActiveStatusState,
            setSelectedAccountId,
            t,
        ]
    )

    return useMemo(
        () => ({ creatingAccount, setActiveStatus, selectAccount, clearSelection, createAccount }),
        [creatingAccount, setActiveStatus, selectAccount, clearSelection, createAccount]
    )
}

export default useAccountSelection
